import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, XCircle, Smartphone, Shield, PenLine, 
  Loader2, AlertTriangle, Clock, Phone, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Composant Canvas pour signature optionnelle
const SignatureCanvas = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onClear();
  };

  const saveSignature = () => {
    if (hasDrawn) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      onSave(dataUrl);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-500">Signature (optionnel) - Dessinez avec votre doigt</p>
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="border-2 border-dashed border-slate-300 rounded-lg w-full touch-none bg-white"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>
          Effacer
        </Button>
        {hasDrawn && (
          <Button type="button" size="sm" onClick={saveSignature} className="bg-blue-600 hover:bg-blue-700">
            <CheckCircle className="h-4 w-4 mr-1" />
            Valider signature
          </Button>
        )}
      </div>
    </div>
  );
};

export default function RemoteSignaturePage() {
  const { dossierId, token } = useParams();
  
  // États
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  
  // OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  // Signature
  const [accepted, setAccepted] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Charger les données de la page
  useEffect(() => {
    const loadPageData = async () => {
      try {
        const res = await fetch(`${API}/api/sav/public/signature/${dossierId}/${token}`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Lien invalide ou expiré');
        }
        const data = await res.json();
        setPageData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadPageData();
  }, [dossierId, token]);

  // Envoyer le code OTP
  const handleSendOtp = async () => {
    setSendingOtp(true);
    try {
      const res = await fetch(`${API}/api/sav/public/signature/${dossierId}/${token}/send-otp`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setOtpSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingOtp(false);
    }
  };

  // Vérifier le code OTP
  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) return;
    
    setVerifyingOtp(true);
    try {
      const res = await fetch(`${API}/api/sav/public/signature/${dossierId}/${token}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: otpCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setOtpVerified(true);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Soumettre la signature
  const handleSubmit = async () => {
    if (!accepted) {
      setError('Vous devez accepter les conditions pour signer.');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/sav/public/signature/${dossierId}/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accepted: true,
          signature: signatureImage
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSuccess(true);
      setSuccessMessage(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // États d'affichage
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-purple-600" />
            <p className="mt-4 text-slate-600">Chargement...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !pageData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="h-16 w-16 mx-auto text-red-500" />
            <h2 className="mt-4 text-xl font-bold text-red-600">Lien invalide</h2>
            <p className="mt-2 text-slate-600">{error}</p>
            <p className="mt-4 text-sm text-slate-500">
              Veuillez contacter PhoneLab pour obtenir un nouveau lien de signature.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-green-600">Signature enregistrée !</h2>
            <p className="mt-3 text-slate-600">{successMessage}</p>
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">
                Dossier : <span className="font-mono font-bold">{dossierId}</span>
              </p>
            </div>
            <p className="mt-6 text-sm text-slate-500">
              Vous pouvez fermer cette page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sigTypeLabel = pageData?.signature_type === 'entry' 
    ? "Signature d'entrée (Dépôt)" 
    : "Signature de sortie (Récupération)";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <div className="text-center py-6">
          <h1 className="text-2xl font-bold text-white">{pageData?.shop_name || 'PhoneLab'}</h1>
          <p className="text-purple-300 mt-1">Signature électronique sécurisée</p>
        </div>

        {/* Info Dossier */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              {sigTypeLabel}
            </CardTitle>
            <CardDescription>
              Dossier n° <span className="font-mono font-bold">{dossierId}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-slate-500">Client</div>
              <div className="font-medium">{pageData?.client_name}</div>
              
              <div className="text-slate-500">Produit</div>
              <div className="font-medium">{pageData?.product_description}</div>
              
              {pageData?.problem_description && (
                <>
                  <div className="text-slate-500">Problème</div>
                  <div className="font-medium">{pageData?.problem_description}</div>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="h-3 w-3" />
              Expire le {new Date(pageData?.expires_at).toLocaleDateString('fr-FR')}
            </div>
          </CardContent>
        </Card>

        {/* Erreur */}
        {error && (
          <div className="p-3 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* Étape 1 : Vérification OTP */}
        {!otpVerified && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Vérification d'identité
              </CardTitle>
              <CardDescription>
                Pour votre sécurité, un code vous sera envoyé par SMS
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Phone className="h-5 w-5 text-slate-400" />
                <span className="text-lg font-mono">{pageData?.masked_phone}</span>
              </div>

              {!otpSent ? (
                <Button 
                  onClick={handleSendOtp} 
                  disabled={sendingOtp}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {sendingOtp ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Smartphone className="h-4 w-4 mr-2" />
                  )}
                  Recevoir le code par SMS
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-green-600 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Code envoyé au {pageData?.masked_phone}
                  </p>
                  
                  <div>
                    <label className="text-sm font-medium">Code à 6 chiffres</label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest font-mono mt-1"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleVerifyOtp}
                    disabled={otpCode.length !== 6 || verifyingOtp}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {verifyingOtp ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Vérifier le code
                  </Button>
                  
                  <Button 
                    variant="link" 
                    onClick={handleSendOtp}
                    disabled={sendingOtp}
                    className="w-full text-sm"
                  >
                    Renvoyer le code
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Étape 2 : Signature (après vérification OTP) */}
        {otpVerified && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PenLine className="h-5 w-5 text-green-600" />
                Signature
              </CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Identité vérifiée
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Texte légal */}
              <div className="p-4 bg-slate-50 rounded-lg border text-sm max-h-40 overflow-y-auto">
                <p className="whitespace-pre-line">{pageData?.legal_text}</p>
              </div>

              {/* Case à cocher obligatoire */}
              <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                <Checkbox
                  id="accept"
                  checked={accepted}
                  onCheckedChange={setAccepted}
                  className="mt-1"
                />
                <label htmlFor="accept" className="text-sm cursor-pointer">
                  <span className="font-semibold">J'ai lu et j'accepte les conditions ci-dessus.</span>
                  <br />
                  <span className="text-slate-500">
                    En cochant cette case, je confirme mon identité et j'accepte de signer électroniquement ce document.
                  </span>
                </label>
              </div>

              {/* Signature optionnelle */}
              <div className="border rounded-lg p-4">
                <SignatureCanvas
                  onSave={setSignatureImage}
                  onClear={() => setSignatureImage(null)}
                />
                {signatureImage && (
                  <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Signature enregistrée
                  </div>
                )}
              </div>

              {/* Bouton de soumission */}
              <Button
                onClick={handleSubmit}
                disabled={!accepted || submitting}
                className="w-full h-14 text-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <PenLine className="h-5 w-5 mr-2" />
                )}
                SIGNER ET VALIDER
              </Button>

              <p className="text-xs text-center text-slate-500">
                Cette signature a la même valeur juridique qu'une signature manuscrite.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-4 text-xs text-slate-400">
          <p>© {new Date().getFullYear()} {pageData?.shop_name || 'PhoneLab'}</p>
          <p>Signature électronique sécurisée par OTP</p>
        </div>
      </div>
    </div>
  );
}
